import React, { useEffect } from "react";
import Button from "@material-ui/core/Button";
import styles from "./SelectSkill.module.css";
import {
    FormControl,
    FilledInput,
    InputAdornment,
    createStyles,
    makeStyles,
    Theme,
    Checkbox,
    FormControlLabel,
    Chip,
    Divider,
    ThemeProvider,
    createTheme,
} from "@material-ui/core";
import Search from "@material-ui/icons/Search";
import { CreateNewSkill, GetSkillList, ResetDuplicateSkillError } from "../../user/UserActions";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../root/RootReducer";
import CheckedIcon from "../../assets/image/CheckedIcon";
import { isNull } from "../../utils/ObjectUtils";
import PuiMessage from "../../components/widgets/PuiMessage";
import { useInitFlag } from "../../components/function/useInitFlag";
import { t } from "i18next";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        searchTitle: {
            fontSize: "16px",
        },
        searchInput: {
            height: "55px",
            borderRadius: "5px",
            padding: "0px 15px 20px",
            fontFamily: "noto-sans-cjk-jp",
            color: "white",
            backgroundColor: "var(--textfield-bg)",
            "&:before": {
                border: "none",
            },
            "&:hover:before": {
                border: "none",
            },
        },
        searchButton: {
            fontFamily: "noto-sans-cjk-jp,sans-serif",
            fontSize: "14px",
            height: "40px",
            width: "200px",
            borderRadius: "5px",
            marginRight: "15px",
            "&:hover": {
                border: "none",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
        },
        addButton: {
            fontSize: "14px",
            borderRadius: "5px",
            "&:hover": {
                border: "none",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
        },
    })
);

const theme = createTheme({
    palette: {
        secondary: {
            main: "#16ADC1",
        },
    },
});

interface SelectSkillProps {
    /* モーダル表示切り替え */
    in: boolean;
    // モーダル閉じる
    onClose?: () => void;
    userName: string;
    userSkills: string[];
    SetUserSkillIds: (userSkillIds: number[]) => void;
    SetIsSaved?: (flg: boolean) => void;
}

const SelectSkill = React.forwardRef<HTMLDivElement, SelectSkillProps>(function SelectSkill(
    props,
    ref
) {
    const { onClose, userName, SetUserSkillIds, userSkills, SetIsSaved } = props;
    const classes = useStyles();
    const dispatch = useDispatch();
    const [editSkills, ChangeEditSkills] = React.useState<string[]>(userSkills);
    const [searchKey, setSearchKey] = React.useState("");
    const { skillList, skillDuplicateError } = useSelector(({ user }: AppState) => user);
    const [displaySkillList, ChangeDisplaySkillList] = React.useState<string[]>([]);
    // const [searchSkill, ChangeSearchSkill] = React.useState<string[]>([]);
    const [isLimitSkill, ChangeLimitSkill] = React.useState(false);
    // 重複エラーメッセージ表示
    const [isShowError, setShowError] = useInitFlag();

    const closeErrorMessage = () => {
        setShowError(false);
        dispatch(ResetDuplicateSkillError());
    };

    const InputSearchSkill = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKey(event.currentTarget.value.trim());
    };

    const DeleteSkill = (deleteSkillName: string) => () => {
        ChangeEditSkills(editSkills.filter((v) => v !== deleteSkillName));
    };

    const CheckSkill = (event: React.ChangeEvent<HTMLInputElement>) => {
        let tempList = editSkills;
        if (!event.currentTarget.checked) {
            tempList = tempList.filter((v) => v !== event.currentTarget.name);
        } else {
            tempList = tempList.concat(event.currentTarget.name);
        }
        ChangeEditSkills(tempList);
    };

    useEffect(() => {
        if (skillDuplicateError !== "") {
            setShowError(true);
        }
        // eslint-disable-next-line
    }, [skillDuplicateError]);

    useEffect(() => {
        if (editSkills.length > 5) {
            ChangeLimitSkill(true);
        } else {
            ChangeLimitSkill(false);
        }
    }, [editSkills]);

    useEffect(() => {
        dispatch(GetSkillList([]));
    }, [dispatch]);

    // 検索していないときはすべてのスキルを表示する、検索時はフィルター結果のみ表示する
    useEffect(() => {
        const skillNameList = skillList?.map(function (row) {
            return row.skill;
        });
        const searchSkill = skillNameList.filter(
            (skillName) => skillName.indexOf(searchKey) !== -1
        );
        ChangeDisplaySkillList(
            searchSkill.length !== 0 || searchKey.length !== 0 ? searchSkill : skillNameList
        );
    }, [skillList, searchKey]);

    // ------------------新規スキル追加 ------------------ //
    const [newSkill, SetNewSkill] = React.useState<string>("");
    const InputAddSkill = (event: React.ChangeEvent<HTMLInputElement>) => {
        SetNewSkill(event.currentTarget.value);
    };

    const AddNewSkill = () => {
        if (newSkill !== "") {
            const addKey = searchKey !== newSkill && newSkill !== "" ? newSkill : searchKey;
            dispatch(CreateNewSkill(addKey));
            if (!editSkills.includes(addKey)) {
                ChangeEditSkills([...editSkills, addKey]);
            }
            SetNewSkill("");
        }
    };

    // スキル名からスキルIDを検索する
    const FindSkillIdBySkillName = (skillName: string) => {
        const skill = skillList.find((row) => row.skill === skillName);
        return skill?.id;
    };

    // スキル名の配列からスキルIDの配列に変換する
    const ChangeSkillNamesToSkillIds = (skillNames: string[]) => {
        const skillIds: number[] = [];
        for (const index in skillNames) {
            const skillId = FindSkillIdBySkillName(skillNames[index]);
            if (!isNull(skillId) && typeof skillId !== "undefined") skillIds.push(skillId);
        }
        return skillIds;
    };

    // 編集完了
    const ConfirmSkill = () => {
        dispatch(ResetDuplicateSkillError());
        if (onClose) {
            if (!isLimitSkill) {
                const skillIds = ChangeSkillNamesToSkillIds(editSkills);
                SetUserSkillIds(skillIds);
                if (SetIsSaved) {
                    SetIsSaved(false);
                }
                onClose();
                SetNewSkill("");
            }
        }
    };

    const handleCancel = () => {
        dispatch(ResetDuplicateSkillError());
        if (onClose) onClose();
    };

    return (
        <>
            <ThemeProvider theme={theme}>
                <div className={styles.root}>
                    <div className={styles.conditionArea}>
                        <div
                            style={{
                                display: "block",
                                marginBottom: "15px",
                                color: "var(--white)",
                            }}
                        >
                            <h2>
                                {t("名前: ")}
                                {userName}
                            </h2>
                            <h2>
                                {t("現在の保有スキル")}( {editSkills.length}/ 5 ):
                            </h2>
                            <div style={{ display: "flex" }}>
                                {editSkills &&
                                    editSkills.map((skill, index) => (
                                        <div key={index} style={{ marginRight: "7px" }}>
                                            <Chip
                                                label={skill}
                                                color="secondary"
                                                style={{
                                                    backgroundColor: "#333A43",
                                                    color: "var(--white)",
                                                }}
                                                className={styles.tag}
                                                size="medium"
                                                onDelete={DeleteSkill(skill)}
                                            />
                                        </div>
                                    ))}
                            </div>
                        </div>
                        <Divider variant="middle" />
                        <h2 style={{ color: "var(--white)" }}> {t("スキルの追加")}</h2>
                    </div>
                    <div className={styles.skillArea}>
                        <h2> {t("■登録されているスキルから追加")}</h2>
                        <FormControl fullWidth variant="filled" color="secondary">
                            <FilledInput
                                id="searchInput"
                                onChange={InputSearchSkill}
                                placeholder="検索したいスキルを入力"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <Search
                                            style={{
                                                color: "var(--white)",
                                                marginTop: "20px",
                                            }}
                                        />
                                    </InputAdornment>
                                }
                                autoComplete={"off"}
                                className={classes.searchInput}
                            />
                        </FormControl>
                        <div className={styles.skillScrollArea}>
                            {displaySkillList.map((item, index) => (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Checkbox
                                            checkedIcon={
                                                <div className={styles.checkedIcon}>
                                                    <CheckedIcon />
                                                </div>
                                            }
                                            name={item}
                                            color="secondary"
                                            checked={editSkills.includes(item)}
                                            onChange={CheckSkill}
                                            style={{ color: "var(--white)" }}
                                        />
                                    }
                                    label={item}
                                    className={styles.skillCheckBox}
                                />
                            ))}
                        </div>
                        <div className={styles.addSkillArea}>
                            <Divider variant="middle" />
                            <h2>{t("■　スキルを新規に作成して追加")}</h2>
                            <FormControl fullWidth variant="filled" color="secondary">
                                <FilledInput
                                    id="newSkillInput"
                                    value={newSkill}
                                    onChange={InputAddSkill}
                                    placeholder="新規スキル名入力"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                className={classes.addButton}
                                                onClick={AddNewSkill}
                                                style={{
                                                    backgroundColor: "var(--studio-btn-bg)",
                                                    color: "var(--white)",
                                                    width: "125px",
                                                    marginTop: "23px",
                                                }}
                                            >
                                                {t("追加")}
                                            </Button>
                                        </InputAdornment>
                                    }
                                    autoComplete={"off"}
                                    className={classes.searchInput}
                                />
                            </FormControl>
                        </div>
                    </div>
                    <div className={styles.btnArea}>
                        {isLimitSkill && (
                            <p
                                style={{
                                    color: "var(--studio-btn-bg)",
                                    fontSize: "15px",
                                    marginRight: "25px",
                                }}
                            >
                                {t("※登録できるスキルは5件までです")}
                            </p>
                        )}
                        <Button
                            variant="outlined"
                            className={classes.searchButton}
                            onClick={handleCancel}
                            style={{
                                marginRight: "10px",
                                color: "var(--loading-bg)",
                                fontSize: "16px",
                                fontFamily: "noto-sans-cjk-jp,sans-serif",
                                border: "solid 2px #5D5D5D",
                            }}
                        >
                            {t("キャンセル")}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            className={classes.searchButton}
                            onClick={ConfirmSkill}
                            disabled={isLimitSkill || editSkills === userSkills}
                            style={{
                                fontSize: "16px",
                                backgroundColor:
                                    isLimitSkill || editSkills === userSkills
                                        ? "var(--button-bg)"
                                        : "var(--studio-btn-bg)",
                                color:
                                    isLimitSkill || editSkills === userSkills
                                        ? "var(--loading-bg)"
                                        : "var(--white)",
                            }}
                        >
                            {t("更新")}
                        </Button>
                    </div>
                    <PuiMessage
                        open={isShowError}
                        type="error"
                        title={skillDuplicateError}
                        duration={5000}
                        onClose={closeErrorMessage}
                    />
                </div>
            </ThemeProvider>
        </>
    );
});

export default SelectSkill;
